import React from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../utils/apiClient';
import { getTimeRangeMonths } from '../utils/dataHelpers';
import { logger } from '../utils/logger';

// Query keys for cache management
export const BRAND_QUERY_KEYS = {
  BRANCHES: 'brandBranches',
  OVERVIEW: 'brandOverview',
  COMPARISON: 'branchComparison',
  SUMMARY: 'branchSummary',
  BRANCH_TREND: 'brandBranchTrend',
  AVERAGE_METRICS: 'brandAverageMetrics',
};

// Type definitions for brand dashboard data
export interface BrandKPIs {
  totalRevenue: number;
  totalCustomers: number;
  avgTransactionValue: number;
  clubMembershipRate: number;
  avgSatisfactionScore: number;
  growthRate: number;
  currency: string;
}

export interface TrendDataPoint {
  month: string;
  value: number;
}

export interface BrandOverviewData {
  kpis: BrandKPIs;
  revenueTrend: TrendDataPoint[];
  customerTrend: TrendDataPoint[];
}

export interface BranchMetrics {
  [key: string]: number;
}

export interface BranchComparisonBranch {
  branchId: number;
  branchName: string;
  currency: string;
  metrics: BranchMetrics;
}

export interface BranchComparisonData {
  branches: BranchComparisonBranch[];
  timeRange: string;
}

export interface BranchSummaryItem {
  branchId: number;
  branchName: string;
  currency: string;
  currentMonthRevenue: number;
  customerCount: number;
  avgTransactionValue: number;
  invoiceCount: number;
  satisfactionScore: number;
  clubMembershipRate: number;
  performanceScore: number;
  subscription?: number;
  subscriptionRate?: number;
  subscription_percentage?: number;
  creditInvoiceCount?: number;
  validCreditInvoiceCount?: number;
  creditInvoiceAmount?: number;
  validCreditInvoiceAmount?: number;
  usedCredit?: number;
  usedCreditAmount?: number;
  sumUsedCredit?: number;
  newCustomers?: number;
  returningCustomers?: number;
  lostCustomers?: number;
}

export interface BrandAverageMetrics extends BranchSummaryItem {
  avgRevenue: number;
  avgCustomers: number;
  avgSatisfaction: number;
  avgMembership: number;
}

export interface BranchSummaryData {
  branches: BranchSummaryItem[];
  brandAverage: BrandAverageMetrics;
  metricAverages?: Record<string, number>;
}

export interface BranchMetricTrendPoint {
  yearmonth: string;
  month: string;
  value: number;
}

export interface BranchMetricTrendSeries {
  branchId: number;
  branchName: string;
  currency: string;
  points: BranchMetricTrendPoint[];
}

export interface BranchMetricTrendData {
  metric: string;
  unit: 'currency' | 'percentage' | 'number';
  currency: string | null;
  labels: string[];
  months: number;
  branches: BranchMetricTrendSeries[];
  brandAverage: BranchMetricTrendSeries;
}

export interface BrandBranch {
  id: number;
  name: string;
  brandId: number;
  currency: string;
}

// API service functions
const apiService = {
  /**
   * Fetch all branches for a brand
   */
  fetchBrandBranches: async (brandId: number): Promise<BrandBranch[]> => {
    const response = await apiClient.get(`/branches/brand/${brandId}`);
    return response.data.map((branch: any) => ({
      id: branch.BranchID,
      name: branch.BranchName,
      brandId: branch.BrandId,
      currency: branch.Currency
    }));
  },

  /**
   * Fetch brand overview data (aggregated KPIs and trends)
   */
  fetchBrandOverview: async (brandId: number, timeRange: string): Promise<BrandOverviewData> => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/brand/${brandId}/overview?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else if (response.data && !response.data.success) {
      // API returned success: false, likely no data
      logger.warn('Brand overview data not available', 'BrandQueries', { brandId, timeRange });
      return {
        kpis: {
          totalRevenue: 0,
          totalCustomers: 0,
          avgTransactionValue: 0,
          clubMembershipRate: 0,
          avgSatisfactionScore: 0,
          growthRate: 0,
          currency: 'تومان'
        },
        revenueTrend: [],
        customerTrend: []
      };
    } else {
      throw new Error('Failed to fetch brand overview data');
    }
  },

  /**
   * Fetch branch comparison data for selected branches and metrics
   */
  fetchBranchComparison: async (
    brandId: number,
    branchIds: number[],
    metrics: string[],
    timeRange: string
  ): Promise<BranchComparisonData> => {
    const months = getTimeRangeMonths(timeRange);
    const branchIdsParam = branchIds.join(',');
    const metricsParam = metrics.join(',');
    
    const response = await apiClient.get(
      `/brand/${brandId}/comparison?branch_ids=${branchIdsParam}&metrics=${metricsParam}&months=${months}`
    );
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else if (response.data && !response.data.success) {
      // API returned success: false, likely no data
      logger.warn('Branch comparison data not available', 'BrandQueries', { brandId, branchIds, metrics, timeRange });
      return {
        branches: [],
        timeRange: `${months} months`
      };
    } else {
      throw new Error('Failed to fetch branch comparison data');
    }
  },

  /**
   * Fetch branch summary data for all branches in a brand
   */
  fetchBranchSummary: async (brandId: number, timeRange: string): Promise<BranchSummaryData> => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/brand/${brandId}/summary?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else if (response.data && !response.data.success) {
      // API returned success: false, likely no data
      logger.warn('Branch summary data not available', 'BrandQueries', { brandId, timeRange });
        return {
          branches: [],
          brandAverage: {
            branchId: 0,
            branchName: 'برند',
            currency: 'تومان',
            currentMonthRevenue: 0,
            customerCount: 0,
            avgRevenue: 0,
            avgCustomers: 0,
            avgTransactionValue: 0,
            invoiceCount: 0,
            subscription: 0,
            creditInvoiceCount: 0,
            creditInvoiceAmount: 0,
            usedCredit: 0,
            avgSatisfaction: 0,
            avgMembership: 0,
            satisfactionScore: 0,
            clubMembershipRate: 0,
            performanceScore: 0,
            newCustomers: 0,
            returningCustomers: 0,
            lostCustomers: 0
          },
          metricAverages: {}
        };
    } else {
      throw new Error('Failed to fetch branch summary data');
    }
  },

  /**
   * Fetch monthly trend data for a single metric across all brand branches
   */
  fetchBrandBranchMetricTrend: async (
    brandId: number,
    metricId: string,
    timeRange: string
  ): Promise<BranchMetricTrendData> => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/brand/${brandId}/branch-trend?metric=${metricId}&months=${months}`);

    if (response.data && response.data.success) {
      return response.data.data;
    } else if (response.data && !response.data.success) {
      logger.warn('Brand branch metric trend not available', 'BrandQueries', { brandId, metricId, timeRange });
      return {
        metric: metricId,
        unit: 'number',
        currency: null,
        labels: [],
        months,
        branches: [],
        brandAverage: {
          branchId: 0,
          branchName: 'میانگین برند',
          currency: 'تومان',
          points: []
        }
      };
    } else {
      throw new Error('Failed to fetch branch metric trend data');
    }
  }
};

/**
 * Hook to fetch branches for a brand with 5-minute cache
 * 
 * Performance optimizations:
 * - 5-minute stale time (branch list rarely changes)
 * - 10-minute garbage collection time
 * - Disabled refetch on window focus
 * - Single retry on failure
 * 
 * @param brandId - The brand ID to fetch branches for
 * @returns Query result with branches data
 */
export function useBrandBranches(brandId: number | null) {
  return useQuery({
    queryKey: [BRAND_QUERY_KEYS.BRANCHES, brandId],
    queryFn: async () => {
      if (!brandId) throw new Error('Brand ID is required');
      
      try {
        return await apiService.fetchBrandBranches(brandId);
      } catch (error) {
        logger.error('Failed to fetch brand branches', 'BrandQueries', error);
        throw error;
      }
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes after becoming stale
    retry: 1,
    refetchOnWindowFocus: false, // Branch list rarely changes, no need to refetch on focus
  });
}

/**
 * Hook to fetch brand overview data (aggregated KPIs and trends)
 * 
 * Performance optimizations:
 * - 5-minute stale time (aggregated data is expensive to compute)
 * - 10-minute garbage collection time
 * - Disabled refetch on window focus
 * - Single retry on failure
 * 
 * @param brandId - The brand ID to fetch overview for
 * @param timeRange - Time range for data (default: 'last6Months')
 * @returns Query result with brand overview data
 */
export function useBrandOverviewData(brandId: number | null, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [BRAND_QUERY_KEYS.OVERVIEW, brandId, timeRange],
    queryFn: async () => {
      if (!brandId) throw new Error('Brand ID is required');
      
      try {
        return await apiService.fetchBrandOverview(brandId, timeRange);
      } catch (error) {
        logger.error('Failed to fetch brand overview data', 'BrandQueries', error);
        throw error;
      }
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes after becoming stale
    retry: 1,
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
  });
}

/**
 * Hook to fetch branch comparison data for multi-branch metric comparisons
 * 
 * Performance optimizations:
 * - Stable query keys using sorted arrays to prevent unnecessary refetches
 * - 3-minute cache for comparison data
 * - Conditional fetching based on valid selections
 * 
 * @param brandId - The brand ID
 * @param branchIds - Array of branch IDs to compare (2-6 branches)
 * @param metrics - Array of metric IDs to compare
 * @param timeRange - Time range for data (default: 'last6Months')
 * @returns Query result with branch comparison data
 */
export function useBranchComparisonData(
  brandId: number | null,
  branchIds: number[],
  metrics: string[],
  timeRange: string = 'last6Months'
) {
  // Sort arrays to create stable query keys (prevents cache misses from array order changes)
  const sortedBranchIds = React.useMemo(() => [...branchIds].sort((a, b) => a - b), [branchIds]);
  const sortedMetrics = React.useMemo(() => [...metrics].sort(), [metrics]);
  
  return useQuery({
    queryKey: [BRAND_QUERY_KEYS.COMPARISON, brandId, sortedBranchIds, sortedMetrics, timeRange],
    queryFn: async () => {
      if (!brandId) throw new Error('Brand ID is required');
      if (sortedBranchIds.length < 2) {
        logger.warn('At least 2 branches required for comparison', 'BrandQueries', { branchIds: sortedBranchIds });
        return {
          branches: [],
          timeRange: `${getTimeRangeMonths(timeRange)} months`
        };
      }
      if (sortedBranchIds.length > 6) {
        logger.warn('Maximum 6 branches allowed for comparison', 'BrandQueries', { branchIds: sortedBranchIds });
        // Take only first 6 branches
        const limitedBranchIds = sortedBranchIds.slice(0, 6);
        return await apiService.fetchBranchComparison(brandId, limitedBranchIds, sortedMetrics, timeRange);
      }
      
      try {
        return await apiService.fetchBranchComparison(brandId, sortedBranchIds, sortedMetrics, timeRange);
      } catch (error) {
        logger.error('Failed to fetch branch comparison data', 'BrandQueries', error);
        throw error;
      }
    },
    enabled: !!brandId && sortedBranchIds.length >= 2 && sortedMetrics.length > 0,
    staleTime: 3 * 60 * 1000, // 3 minutes cache (shorter for comparison data)
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes after becoming stale
    retry: 1,
    refetchOnWindowFocus: false, // Prevent refetch on window focus for better performance
  });
}

/**
 * Hook to fetch branch summary data for all branches in a brand
 * 
 * Performance optimizations:
 * - 5-minute stale time (summary data is relatively stable)
 * - 10-minute garbage collection time
 * - Disabled refetch on window focus
 * - Single retry on failure
 * 
 * @param brandId - The brand ID
 * @param timeRange - Time range for data (default: 'last6Months')
 * @returns Query result with branch summary data
 */
export function useBranchSummaryData(brandId: number | null, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [BRAND_QUERY_KEYS.SUMMARY, brandId, timeRange],
    queryFn: async () => {
      if (!brandId) throw new Error('Brand ID is required');
      
      try {
        return await apiService.fetchBranchSummary(brandId, timeRange);
      } catch (error) {
        logger.error('Failed to fetch branch summary data', 'BrandQueries', error);
        throw error;
      }
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes after becoming stale
    retry: 1,
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
  });
}

/**
 * Hook to fetch per-branch monthly trend for one selected metric
 */
export function useBrandBranchMetricTrend(
  brandId: number | null,
  metricId: string | null,
  timeRange: string = 'last6Months'
) {
  return useQuery({
    queryKey: [BRAND_QUERY_KEYS.BRANCH_TREND, brandId, metricId, timeRange],
    queryFn: async () => {
      if (!brandId || !metricId) {
        throw new Error('Brand ID and metric ID are required');
      }

      try {
        return await apiService.fetchBrandBranchMetricTrend(brandId, metricId, timeRange);
      } catch (error) {
        logger.error('Failed to fetch brand branch metric trend', 'BrandQueries', error);
        throw error;
      }
    },
    enabled: !!brandId && !!metricId,
    staleTime: 3 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch brand average metrics (extracted from summary data)
 * This is a convenience hook that extracts just the average metrics
 * 
 * Performance optimizations:
 * - 5-minute stale time (matches summary data cache)
 * - 10-minute garbage collection time
 * - Disabled refetch on window focus
 * - Single retry on failure
 * 
 * @param brandId - The brand ID
 * @param timeRange - Time range for data (default: 'last6Months')
 * @returns Query result with brand average metrics
 */
export function useBrandAverageMetrics(brandId: number | null, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [BRAND_QUERY_KEYS.AVERAGE_METRICS, brandId, timeRange],
    queryFn: async () => {
      if (!brandId) throw new Error('Brand ID is required');
      
      try {
        const summaryData = await apiService.fetchBranchSummary(brandId, timeRange);
        return summaryData.brandAverage;
      } catch (error) {
        logger.error('Failed to fetch brand average metrics', 'BrandQueries', error);
        throw error;
      }
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes after becoming stale
    retry: 1,
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
  });
}

// Enhanced types for new features
export interface BestBranchInfo {
  branchId: number;
  branchName: string;
  value: number;
  currency: string;
}

export interface EnhancedBrandKPIs {
  brandMetrics: BrandKPIs;
  bestBranches: {
    revenue?: BestBranchInfo;
    customers?: BestBranchInfo;
    avgTransaction?: BestBranchInfo;
    clubMembership?: BestBranchInfo;
    satisfaction?: BestBranchInfo;
  };
}

export interface SatisfactionDataPoint {
  month: string;
  satisfactionRate: number;
  totalSurveys: number;
}

export interface ProductDistributionItem {
  category: string;
  quantity: number;
  percentage: number;
}

export interface CustomerRetentionDataPoint {
  month: string;
  totalCustomers: number;
  newCustomers: number;
  returningCustomers: number;
  lostCustomers: number;
  retentionRate: number;
}

// Enhanced API service functions
const enhancedApiService = {
  /**
   * Fetch enhanced brand overview with best branch info
   */
  fetchEnhancedBrandOverview: async (brandId: number, timeRange: string): Promise<EnhancedBrandKPIs> => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/brand/${brandId}/overview-enhanced?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch enhanced brand overview data');
    }
  },

  /**
   * Fetch brand satisfaction analysis
   */
  fetchBrandSatisfactionAnalysis: async (brandId: number, timeRange: string): Promise<SatisfactionDataPoint[]> => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/brand/${brandId}/satisfaction-analysis?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      return [];
    }
  },

  /**
   * Fetch brand product distribution
   */
  fetchBrandProductDistribution: async (brandId: number, timeRange: string): Promise<ProductDistributionItem[]> => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/brand/${brandId}/product-distribution?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      return [];
    }
  },

  /**
   * Fetch brand customer retention
   */
  fetchBrandCustomerRetention: async (brandId: number, timeRange: string): Promise<CustomerRetentionDataPoint[]> => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/brand/${brandId}/customer-retention?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      return [];
    }
  }
};

/**
 * Hook to fetch enhanced brand overview with best branch information
 */
export function useEnhancedBrandOverview(brandId: number | null, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: ['brandOverviewEnhanced', brandId, timeRange],
    queryFn: async () => {
      if (!brandId) throw new Error('Brand ID is required');
      
      try {
        return await enhancedApiService.fetchEnhancedBrandOverview(brandId, timeRange);
      } catch (error) {
        logger.error('Failed to fetch enhanced brand overview', 'BrandQueries', error);
        throw error;
      }
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch brand satisfaction analysis
 */
export function useBrandSatisfactionAnalysis(brandId: number | null, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: ['brandSatisfactionAnalysis', brandId, timeRange],
    queryFn: async () => {
      if (!brandId) throw new Error('Brand ID is required');
      
      try {
        return await enhancedApiService.fetchBrandSatisfactionAnalysis(brandId, timeRange);
      } catch (error) {
        logger.error('Failed to fetch brand satisfaction analysis', 'BrandQueries', error);
        throw error;
      }
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch brand product distribution
 */
export function useBrandProductDistribution(brandId: number | null, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: ['brandProductDistribution', brandId, timeRange],
    queryFn: async () => {
      if (!brandId) throw new Error('Brand ID is required');
      
      try {
        return await enhancedApiService.fetchBrandProductDistribution(brandId, timeRange);
      } catch (error) {
        logger.error('Failed to fetch brand product distribution', 'BrandQueries', error);
        throw error;
      }
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch brand customer retention
 */
export function useBrandCustomerRetention(brandId: number | null, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: ['brandCustomerRetention', brandId, timeRange],
    queryFn: async () => {
      if (!brandId) throw new Error('Brand ID is required');
      
      try {
        return await enhancedApiService.fetchBrandCustomerRetention(brandId, timeRange);
      } catch (error) {
        logger.error('Failed to fetch brand customer retention', 'BrandQueries', error);
        throw error;
      }
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}
